import { base64ListRequest } from "@/services/backend/_lib/base64-list-request";
import { backendApi } from "@/services/backend/api";
import {
  DeleteGfsCatalogItemRequest,
  DeleteGfsCatalogItemImageRequest,
  DeleteGfsCatalogRequest,
  GfsCatalog,
  GfsCatalogItemRefreshPreview,
  GfsCatalogListResponse,
  ListGfsCatalogRequest,
  LoadGfsCatalogRequest,
  PutGfsCatalogItemRequest,
  PutGfsCatalogRequest,
  RefreshGfsCatalogItemRequest,
  ShowGfsCatalogItemImageRequest,
  UpdateGfsCatalogItemRequest,
  UploadGfsCatalogItemImageRequest,
  PutGfsCatalogConsumableRequest,
  DeleteGfsCatalogConsumableRequest,
} from "@/services/backend/gfs/catalog/types";

const gfsCatalogApi = backendApi
  .enhanceEndpoints({
    addTagTypes: ["GfsCatalog", "GfsCatalogs"],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      gfsCatalogPut: build.mutation<GfsCatalog, PutGfsCatalogRequest>({
        query: (body) => ({
          url: "/gfs/catalogs",
          method: "PUT",
          body,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "GfsCatalog", id: request.id },
          { type: "GfsCatalogs", id: "LIST" },
        ],
      }),
      gfsCatalogGet: build.query<GfsCatalog, LoadGfsCatalogRequest>({
        query: (params) => ({
          url: "/gfs/catalogs/get",
          method: "GET",
          params,
        }),
        providesTags: (_result, _error, request) => [
          { type: "GfsCatalog", id: request.id },
        ],
      }),
      gfsCatalogList: build.query<
        GfsCatalogListResponse,
        ListGfsCatalogRequest
      >({
        query: (params) => ({
          url: "/gfs/catalogs/list",
          method: "GET",
          params: base64ListRequest(params),
        }),
        providesTags: (result) =>
          result
            ? [
                { type: "GfsCatalogs", id: "LIST" },
                ...result.items.map((catalog) => ({
                  type: "GfsCatalog" as const,
                  id: catalog.id,
                })),
              ]
            : [{ type: "GfsCatalogs", id: "LIST" }],
      }),
      gfsCatalogDelete: build.mutation<void, DeleteGfsCatalogRequest>({
        query: (params) => ({
          url: "/gfs/catalogs",
          method: "DELETE",
          params,
        }),
        invalidatesTags: [{ type: "GfsCatalogs", id: "LIST" }],
      }),
      gfsCatalogItemPut: build.mutation<GfsCatalog, PutGfsCatalogItemRequest>({
        query: (body) => ({
          url: "/gfs/catalogs/items",
          method: "PUT",
          body,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "GfsCatalog", id: request.catalogId },
          { type: "GfsCatalogs", id: "LIST" },
        ],
      }),
      gfsCatalogItemUpdate: build.mutation<
        GfsCatalog,
        UpdateGfsCatalogItemRequest
      >({
        query: (body) => ({
          url: "/gfs/catalogs/items/details",
          method: "PUT",
          body,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "GfsCatalog", id: request.catalogId },
          { type: "GfsCatalogs", id: "LIST" },
        ],
      }),
      gfsCatalogItemRefreshPreview: build.query<
        GfsCatalogItemRefreshPreview,
        RefreshGfsCatalogItemRequest
      >({
        query: (params) => ({
          url: "/gfs/catalogs/items/refresh",
          method: "GET",
          params,
        }),
        providesTags: (_result, _error, request) => [
          { type: "GfsCatalog", id: request.catalogId },
        ],
      }),
      gfsCatalogItemRefresh: build.mutation<
        GfsCatalog,
        RefreshGfsCatalogItemRequest
      >({
        query: (body) => ({
          url: "/gfs/catalogs/items/refresh",
          method: "PUT",
          body,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "GfsCatalog", id: request.catalogId },
          { type: "GfsCatalogs", id: "LIST" },
        ],
      }),
      gfsCatalogItemDelete: build.mutation<
        GfsCatalog,
        DeleteGfsCatalogItemRequest
      >({
        query: (params) => ({
          url: "/gfs/catalogs/items",
          method: "DELETE",
          params,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "GfsCatalog", id: request.catalogId },
          { type: "GfsCatalogs", id: "LIST" },
        ],
      }),
      gfsCatalogConsumablePut: build.mutation<
        GfsCatalog,
        PutGfsCatalogConsumableRequest
      >({
        query: (body) => ({
          url: "/gfs/catalogs/consumables",
          method: "PUT",
          body,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "GfsCatalog", id: request.catalogId },
          { type: "GfsCatalogs", id: "LIST" },
        ],
      }),
      gfsCatalogConsumableDelete: build.mutation<
        GfsCatalog,
        DeleteGfsCatalogConsumableRequest
      >({
        query: (body) => ({
          url: "/gfs/catalogs/consumables",
          method: "DELETE",
          body,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "GfsCatalog", id: request.catalogId },
          { type: "GfsCatalogs", id: "LIST" },
        ],
      }),
      gfsCatalogItemImage: build.mutation<
        GfsCatalog,
        UploadGfsCatalogItemImageRequest
      >({
        query: (request) => {
          const body = new FormData();
          body.append("catalogId", request.catalogId);
          body.append("itemId", request.itemId);
          body.append("fileId", request.fileId);
          body.append("file", request.file || "");
          return {
            url: "/gfs/catalogs/items/image",
            method: "POST",
            body,
            formData: true,
          };
        },
        invalidatesTags: (_result, _error, request) => [
          { type: "GfsCatalog", id: request.catalogId },
          { type: "GfsCatalogs", id: "LIST" },
        ],
      }),
      gfsCatalogItemDeleteImage: build.mutation<
        GfsCatalog,
        DeleteGfsCatalogItemImageRequest
      >({
        query: (body) => ({
          url: "/gfs/catalogs/items/image",
          method: "DELETE",
          body,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "GfsCatalog", id: request.catalogId },
          { type: "GfsCatalogs", id: "LIST" },
        ],
      }),
      gfsCatalogItemShowImage: build.query<
        { objectURL: string },
        ShowGfsCatalogItemImageRequest
      >({
        query: (request) => ({
          url: "/gfs/catalogs/items/image",
          method: "GET",
          params: request,
          responseHandler: async (response) => {
            if (response.status !== 200) {
              return response.json();
            }
            return {
              objectURL: window.URL.createObjectURL(await response.blob()),
            };
          },
          cache: "no-cache",
        }),
      }),
    }),
  });

export const {
  useGfsCatalogPutMutation,
  useGfsCatalogGetQuery,
  useGfsCatalogListQuery,
  useGfsCatalogDeleteMutation,
  useGfsCatalogItemPutMutation,
  useGfsCatalogItemUpdateMutation,
  useGfsCatalogItemRefreshPreviewQuery,
  useGfsCatalogItemRefreshMutation,
  useGfsCatalogItemDeleteMutation,
  useGfsCatalogConsumablePutMutation,
  useGfsCatalogConsumableDeleteMutation,
  useGfsCatalogItemImageMutation,
  useGfsCatalogItemDeleteImageMutation,
  useGfsCatalogItemShowImageQuery,
} = gfsCatalogApi;
